// UltraFlex Admin — Application Review Queue
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  ClipboardList, Check, X, AlertTriangle, Clock, Eye,
  UserCheck, UserX, ShieldAlert, ChevronDown, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-500/15 text-yellow-400' },
  flagged: { label: 'Flagged', color: 'bg-red-500/15 text-red-400' },
  approved: { label: 'Approved', color: 'bg-emerald-500/15 text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-400' },
};

const AdminApplicationReview = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('flagged');
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API_URL}/api/banned/applications?status=${statusFilter}`
        : `${API_URL}/api/banned/applications`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setApplications(data.items || []);
      setTotal(data.total || 0);
    } catch { setApplications([]); }
    setLoading(false);
  }, [statusFilter, token]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleReview = async (appId, decision) => {
    try {
      await fetch(`${API_URL}/api/banned/applications/${appId}/review`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ decision, notes: reviewNotes })
      });
      setReviewingId(null); setReviewNotes('');
      fetchApps();
    } catch {}
  };

  const flaggedCount = applications.filter(a => a.status === 'flagged').length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-applications-page">
      <div>
        <h1 className="text-2xl font-bold text-uf-text" data-testid="applications-page-title">Application Review Queue</h1>
        <p className="text-uf-text-muted text-sm mt-1">
          Membership applications flagged by auto-screening against the banned list.
        </p>
      </div>

      {/* Flagged Alert Banner */}
      {flaggedCount > 0 && statusFilter === 'flagged' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3" data-testid="flagged-alert">
          <ShieldAlert className="w-5 h-5 text-red-400" />
          <p className="text-red-400 text-sm font-medium">{flaggedCount} application{flaggedCount > 1 ? 's' : ''} require{flaggedCount === 1 ? 's' : ''} review</p>
        </div>
      )}

      {/* Status Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {['flagged', 'pending', 'approved', 'rejected', ''].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              statusFilter === s ? 'bg-uf-red/15 text-uf-red' : 'bg-uf-surface text-uf-text-muted hover:text-uf-text border border-uf-border'
            }`}>
            {s ? STATUS_CONFIG[s]?.label : 'All'}{s === 'flagged' && flaggedCount > 0 ? ` (${flaggedCount})` : ''}
          </button>
        ))}
        <span className="text-uf-text-muted text-xs ml-auto">{total} total</span>
      </div>

      {/* Applications List */}
      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center" data-testid="no-applications">
          <ClipboardList className="w-12 h-12 text-uf-text-muted mx-auto mb-3 opacity-40" />
          <p className="text-uf-text-muted text-sm">No applications to review.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="applications-list">
          {applications.map(app => {
            const sc = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
            return (
              <div key={app.id} data-testid={`app-card-${app.id}`}
                className={`bg-uf-surface rounded-xl border p-5 ${
                  app.status === 'flagged' ? 'border-red-500/30' : 'border-uf-border'
                }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    app.status === 'flagged' ? 'bg-red-500/15' : app.status === 'approved' ? 'bg-emerald-500/15' : 'bg-yellow-500/15'
                  }`}>
                    {app.status === 'flagged' ? <ShieldAlert className="w-5 h-5 text-red-400" /> :
                     app.status === 'approved' ? <UserCheck className="w-5 h-5 text-emerald-400" /> :
                     <Clock className="w-5 h-5 text-yellow-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-uf-text font-semibold text-sm">{app.first_name} {app.last_name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${sc.color}`}>{sc.label}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-uf-text-muted text-xs">
                      <span>{app.email}</span>
                      {app.phone && <span>{app.phone}</span>}
                      {app.date_of_birth && <span>DOB: {app.date_of_birth}</span>}
                    </div>
                    {app.flag_reason && (
                      <div className="mt-2 bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
                        <p className="text-red-400 text-xs font-medium flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Screening Match
                        </p>
                        <p className="text-uf-text-muted text-xs mt-1">{app.flag_reason}</p>
                      </div>
                    )}
                    {app.review_notes && (
                      <p className="text-uf-text-muted text-xs mt-2 italic flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> {app.review_notes}
                      </p>
                    )}
                    <p className="text-uf-text-muted text-[10px] mt-2">
                      Applied: {new Date(app.created_at).toLocaleString('en-GB')}
                      {app.reviewed_at && ` | Reviewed: ${new Date(app.reviewed_at).toLocaleString('en-GB')}`}
                    </p>
                  </div>

                  {/* Review Actions */}
                  {(app.status === 'flagged' || app.status === 'pending') && (
                    <div className="flex items-center gap-1">
                      {reviewingId !== app.id ? (
                        <>
                          <button onClick={() => handleReview(app.id, 'approved')} data-testid={`approve-app-${app.id}`}
                            className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg" title="Approve">
                            <Check className="w-5 h-5" />
                          </button>
                          <button onClick={() => setReviewingId(app.id)} data-testid={`review-app-${app.id}`}
                            className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg" title="Reject with notes">
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                            placeholder="Rejection reason..."
                            className="bg-uf-surface-hi border border-uf-border rounded-lg px-2 py-1 text-uf-text text-xs w-48"
                            data-testid={`review-notes-${app.id}`} />
                          <div className="flex gap-1">
                            <button onClick={() => handleReview(app.id, 'rejected')} data-testid={`reject-app-${app.id}`}
                              className="flex-1 bg-red-500/15 text-red-400 px-2 py-1 rounded text-xs">Reject</button>
                            <button onClick={() => setReviewingId(null)}
                              className="flex-1 bg-uf-surface-hi text-uf-text-muted px-2 py-1 rounded text-xs">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminApplicationReview;
